import React from 'react';
import { PageHeader } from '../components/ui';

/** Fixed date of the last wording change — was `new Date()`, which showed every
 *  visitor "today" and made the terms look like they changed on every visit. */
const LAST_UPDATED = 'September 26, 2026';

function Section({ n, title, children }) {
  return (
    <section className="rounded-2xl border border-slate-700/60 bg-gradient-to-br from-slate-900 via-slate-900 to-slate-800 p-4 text-slate-300 shadow-md">
      <h2 className="font-bold text-white mb-2">{n}. {title}</h2>
      {children}
    </section>
  );
}

export default function LegalTerms() {
  return (
    <div className="pb-8">
      <PageHeader title="Terms & Legal Disclaimer" subtitle={`Last updated: ${LAST_UPDATED}`} />

      <div className="space-y-4 text-sm leading-relaxed">
        <section className="bg-amber-50 border border-amber-200 rounded-2xl p-4 text-amber-950">
          <p className="font-bold text-amber-900 mb-2">Binding agreement</p>
          <p>
            By registering for, accessing, or using StockXignal (the &quot;Platform&quot;), you acknowledge that you have
            read, understood, and <strong>agree to be legally bound</strong> by these Terms &amp; Legal Disclaimer.
            If you do not agree, you must not use the Platform.
          </p>
        </section>

        <Section n={1} title="Informational purposes only — not personalized advice">
          <p>
            Content on this Platform—including stock and option signals, prices, charts, news, AI suggestions,
            education, and performance figures—is for <strong>informational purposes only</strong>.
            You are solely responsible for your own investment and trading decisions.
          </p>
          <p className="mt-2">
            Nothing on the Platform is personalized investment advice for any particular user, and nothing on it is
            an offer or solicitation to buy or sell any security or option. Signals are general in nature and do not
            consider your financial situation, objectives, or risk tolerance.
          </p>
        </Section>

        <Section n={2} title="Not an investment adviser or broker">
          <p>
            StockXignal and its owners, operators, and administrators are <strong>not registered</strong> as an
            investment adviser, investment adviser representative, broker-dealer, or broker with the U.S. Securities
            and Exchange Commission, FINRA, any state securities regulator, or any other regulatory authority. The
            Platform does not execute trades, hold funds, or manage accounts for anyone.
          </p>
        </Section>

        <Section n={3} title="AI, automated, and backtested results">
          <p>
            Many signals on the Platform are produced by automated rules and AI models, and some figures are based on
            <strong> backtested or hypothetical</strong> results—a strategy applied after the fact to historical data.
            Hypothetical results have inherent limitations: they do not represent actual trading and may not reflect
            real-world conditions such as slippage, fills, liquidity, fees, delayed data, or the emotional factors that
            affect real decisions.
          </p>
          <p className="mt-2">
            Performance shown on the Platform is <strong>not verified and not typical</strong>. We do not track the
            individual trading results of our users and cannot say what results a typical user achieves. Your actual
            results will vary with your experience, timing, position size, risk management, and market conditions.
            It should not be assumed that any signal, method, or indicator will be profitable or will not result in
            losses.
          </p>
        </Section>

        <Section n={4} title="No guarantee of results">
          <p>
            Past performance does not guarantee future results. Signals may be delayed, incomplete, or incorrect.
            Market data and option estimates may be wrong or unavailable. You accept full responsibility for
            verifying all information before acting.
          </p>
        </Section>

        <Section n={5} title="Seek professional and tax advice">
          <p>
            Use the Platform only as a starting point for your own independent research. Before acting on any
            information, consider seeking advice from a licensed financial, tax, or legal professional. Tax
            consequences have not been considered in any signal or content.
          </p>
        </Section>

        <Section n={6} title="Market data, delays, and service interruptions">
          <p>
            Market data comes from third-party providers and may be <strong>delayed</strong> (for example, option
            data may be ~15 minutes delayed), estimated, incomplete, or temporarily unavailable. Information reflects
            conditions at the time it was published and may not reflect current prices. Push notifications, alerts,
            and pages may arrive late or not at all.
          </p>
          <p className="mt-2">
            The Platform may experience outages, errors, or interruptions. StockXignal is not responsible for losses
            resulting from delayed, missing, or inaccurate data, or from technical failures or service disruptions.
          </p>
        </Section>

        <Section n={7} title="Conflicts of interest">
          <p>
            StockXignal, its owners, operators, and administrators may, at any time, buy, sell, or hold long or short
            positions in securities or options that appear in signals or other content on the Platform—including
            before, during, or after a signal is published—and may trade in a direction different from a signal. You
            should assume such positions may exist.
          </p>
          <p className="mt-2">
            Signals are not paid promotions. If StockXignal ever receives compensation from a company or issuer in
            connection with content about its securities, that will be disclosed with the relevant content.
          </p>
        </Section>

        <Section n={8} title="Forward-looking statements">
          <p>
            Some content may contain forward-looking statements—words such as &quot;expects,&quot; &quot;targets,&quot;
            &quot;projects,&quot; or &quot;believes.&quot; These are estimates, not facts, and involve risks and
            uncertainties that may cause actual outcomes to differ materially. We do not undertake to update them.
            Public company filings are available on the SEC&apos;s EDGAR database at sec.gov.
          </p>
        </Section>

        <Section n={9} title="Eligibility">
          <p>
            You must be at least 18 years old (or the age of majority where you live) and legally able to enter into
            this agreement to use the Platform. By registering, you confirm that you meet these requirements.
          </p>
        </Section>

        <Section n={10} title="Subscriptions, free trial, and payments">
          <p>
            New accounts receive a <strong>7-day free trial</strong>. After the trial, continued access requires a
            paid plan. Plans are <strong>one-time payments</strong> that give access for the period shown at checkout;
            they <strong>do not renew automatically</strong>. When a trial or paid period ends, access ends until you
            purchase a new plan.
          </p>
          <p className="mt-2">
            <strong>No commitment:</strong> there is no contract term, cancellation fee, or recurring charge. You can
            stop at any time simply by not purchasing another plan—nothing further is charged.
          </p>
          <p className="mt-2">
            <strong>No refunds:</strong> all payments are final and non-refundable, including for any unused time
            remaining in a paid period, except where a refund is required by law. Payments are processed by our
            payment provider (Stripe); StockXignal does not store full card numbers. Prices shown at checkout apply
            to that purchase, and prices for future periods may change. Access may be suspended or ended without
            refund if these terms are violated.
          </p>
        </Section>

        <Section n={11} title="Data privacy">
          <p>
            We collect account and usage data needed to operate the Platform (for example email, phone, subscription
            status, and login activity). We will not sell, rent, or misuse your personal data. We do not share your
            personal information with third parties for their marketing. Access to user data is limited to authorized
            operators for support, billing, and security. You agree not to attempt to access, scrape, or misuse other
            users&apos; data. If you believe your data has been compromised, contact support promptly.
          </p>
        </Section>

        <Section n={12} title="Notifications and communications">
          <p>
            By enabling notifications or providing your email or phone number, you agree to receive signal alerts,
            account, billing, and service messages from StockXignal. You can turn off push notifications in the app
            or your device settings at any time. Alerts are a convenience only—they may be delayed or not delivered,
            and you should not rely on them for time-sensitive trading decisions.
          </p>
        </Section>

        <Section n={13} title="Third-party content & links">
          <p>
            The app may display third-party news, market data, charts, and external links. We are not responsible for
            the content, accuracy, or privacy policies of those third parties. Your use of third-party sites or
            services is at your own risk and subject to their terms.
          </p>
        </Section>

        <Section n={14} title="Intellectual property">
          <p>
            The Platform and its content—including signals, analysis, software, design, text, and the StockXignal name
            and logo—are owned by StockXignal or its licensors and protected by intellectual property laws. Your
            subscription gives you a personal, non-transferable, revocable right to view the content for your own
            use; it does not transfer any ownership to you.
          </p>
        </Section>

        <Section n={15} title="Your sole risk — no liability for losses or profits">
          <p>
            <strong>You alone are responsible for any profits or losses</strong> arising from trading or investment
            decisions made after viewing this Platform. To the maximum extent permitted by law, StockXignal, its
            owners, operators, administrators, affiliates, and suppliers are <strong>not liable</strong> for any
            direct, indirect, incidental, special, consequential, punitive, or exemplary damages, including but not
            limited to loss of capital, trading losses, lost profits, lost data, or business interruption, whether
            based on contract, tort, negligence, strict liability, or otherwise—even if advised of the possibility
            of such damages.
          </p>
        </Section>

        <Section n={16} title="Assumption of risk">
          <p>
            Trading stocks and options involves substantial risk of loss and is not suitable for every investor.
            You may lose some, all, or—with some option or short positions—more than your original investment.
            You represent that you understand these risks and that any capital you use is capital you can afford
            to lose. You will not hold the Platform responsible for outcomes of your trades.
          </p>
        </Section>

        <Section n={17} title="User conduct & account">
          <p>
            You agree to provide accurate registration information, keep your credentials secure, and use the
            Platform only for lawful purposes. You may not share your account, or copy, resell, or redistribute
            signals or other Platform content; scrape or automate access to the Platform; upload malicious code;
            harass other users; or attempt to damage, disable, or gain unauthorized access to the Platform. We may
            suspend or terminate access at any time for any reason, including violation of these terms.
          </p>
        </Section>

        <Section n={18} title="No warranties">
          <p>
            The Platform is provided <strong>&quot;AS IS&quot; and &quot;AS AVAILABLE&quot;</strong> without warranties of any kind,
            express or implied, including merchantability, fitness for a particular purpose, and non-infringement.
            We do not warrant uninterrupted, error-free, or secure service.
          </p>
        </Section>

        <Section n={19} title="Indemnification">
          <p>
            You agree to indemnify and hold harmless StockXignal and its operators from any claims, damages,
            losses, and expenses (including reasonable legal fees) arising from your use of the Platform, your
            trading activity, or your breach of these terms.
          </p>
        </Section>

        <Section n={20} title="Events beyond our control">
          <p>
            StockXignal is not liable for any failure or delay caused by circumstances beyond its reasonable control,
            including natural disasters, war, terrorism, government action, exchange or market-data outages, or
            internet, hosting, or telecommunications failures.
          </p>
        </Section>

        <Section n={21} title="Intended for U.S. residents">
          <p>
            The Platform is intended primarily for residents of the United States. Securities laws differ by country,
            and the content may not be appropriate or lawful for residents of other jurisdictions. If you use the
            Platform from outside the U.S., you are responsible for complying with your local laws.
          </p>
        </Section>

        <Section n={22} title="Changes to these terms">
          <p>
            We may update these terms from time to time. The &quot;Last updated&quot; date at the top shows when they
            last changed. Continuing to use the Platform after an update means you accept the updated terms; if you
            do not agree, stop using the Platform.
          </p>
        </Section>

        <Section n={23} title="Severability">
          <p>
            If any part of these terms is found invalid or unenforceable, the rest remains in full effect, and the
            affected part will be limited to the minimum extent necessary to make it enforceable.
          </p>
        </Section>

        <Section n={24} title="Contact">
          <p>
            Questions about these terms, your account, or billing can be sent through the Help page in the app.
          </p>
        </Section>

        <Section n={25} title="Agreement by use">
          <p>
            Creating an account, clicking register, logging in, or continuing to use the Platform constitutes
            your <strong>electronic acceptance</strong> of these Terms &amp; Legal Disclaimer. You waive any
            argument that you did not read or understand these terms.
          </p>
        </Section>
      </div>
    </div>
  );
}
